import React from 'react';
import type { MiniTvRunPhase } from '../stores/mini-tv-run-store';
import type { UseMiniTvControllerReturn } from './useMiniTvController';
import { liveUntilSecFromEventLike } from 'features/realtime/liveFromResults';
import { getEventIdForDisplay, safeEventIdForLabel } from 'features/events/eventId';

const MINI_TV_MIN_PRESENTATION_MS = 2000;
const MINI_TV_JOIN_LATE_GRACE_SEC = 5;
const PRESENTATION_VIDEO_LOAD_GRACE_MS = 10000;

const RACING_SPORT_IDS = [10, 11, 20, 21, 30];
const BASEBALL_SPORT_ID = 40;
const FOOTBALL_SPORT_ID = 50;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface JoinPosition {
  elapsed: number;
  joiningLate: boolean;
  initialSeekTime: number | null;
  initialSegmentIndex: number | null;
  initialSegmentSeekSeconds: number | null;
}

export interface AdvanceCursorParams {
  eventId?: string | number | null;
  eventTimestamp?: number | null;
  week?: number | null;
}

export interface RunPayload extends Record<string, unknown> {
  eventId: string | number | null;
  scheduleId: string | number | null;
  sportId: number | string | null;
  event: unknown;
  eventResults: Record<string, unknown>;
  videoUrl: string | null;
  videoUrls: string[] | null;
  avh: unknown;
  showevolution?: number;
  joinElapsedSeconds: number;
  initialSeekTime?: number | null;
  initialSegmentIndex?: number | null;
  initialSegmentSeekSeconds?: number | null;
  otherWeekEvents?: unknown[];
  weekResults?: Record<string, unknown>;
}

export interface DisplayExecutionRun {
  scheduleId: string | number;
  run: RunPayload | MiniTvRunPhase;
}

export interface UseMiniTvOrchestratorParams {
  scheduleId: string | number | null;
  sportId: number | string | null;
  eventStarted: boolean;
  run: MiniTvRunPhase | null;
  activeExecutionRuns: DisplayExecutionRun[];
  miniTv: UseMiniTvControllerReturn;
  miniTvCtxKey: string;
  miniTvCtxKeyRef: React.MutableRefObject<string>;
  liveEventId: string | number | null;
  nextEventId: string | number | null;
  nextEvent: Record<string, unknown> | null;
  tvAccordionExpanded: boolean;
  setTvAccordionExpanded: (v: boolean | ((prev: boolean) => boolean)) => void;
  isActiveLayout: boolean;
  isTabVisible: boolean;
  isCancelled: boolean;
  isSegmentBasedSport: boolean;
  useBatchVod: boolean;
  effectiveEventResults: Record<string, unknown>;
  hasOngoingCurrent: boolean;
  ongoingPayload: Record<string, unknown> | null;
  nowSecRef: React.MutableRefObject<number>;
  startRun: (scheduleId: string | number, payload: Record<string, unknown>) => void;
  advanceCursorAfterExecution: (params: AdvanceCursorParams) => void;
  maybeRefetchUpcoming?: () => void;
  footballOtherWeekEvents: unknown[];
  footballWeekResults: Record<string, unknown>;
  pollExhausted: boolean;
}

export interface UseMiniTvOrchestratorResult {
  displayExecutionRuns: DisplayExecutionRun[];
  shouldShowCancelledOverlay: boolean;
  shouldShowVideoErrorOverlay: boolean;
  shouldShowResultsFetchError: boolean;
  hasVideoUrls: boolean;
  joinPositionForPresentation: JoinPosition;
  hasValidJoinPositionData: boolean;
  resetOrchestrator: () => void;
}

// ---------------------------------------------------------------------------
// computeJoinPosition
// ---------------------------------------------------------------------------

export function computeJoinPosition(
  eventResults: Record<string, unknown> | null | undefined,
  serverNowSec: number,
  sportId: number | string | null,
): JoinPosition {
  const eventTs = eventResults?.event_timestamp != null ? Number(eventResults.event_timestamp) : null;
  let duration = eventResults?.duration != null ? Number(eventResults.duration) : null;
  if (duration == null && eventTs != null && eventResults?.end_timestamp != null && Number.isFinite(Number(eventResults.end_timestamp))) {
    const endTs = Number(eventResults.end_timestamp);
    if (endTs > eventTs!) duration = endTs - eventTs!;
  }
  if (eventTs == null || duration == null || !Number.isFinite(eventTs) || !Number.isFinite(duration)) {
    return { elapsed: 0, joiningLate: false, initialSeekTime: null, initialSegmentIndex: null, initialSegmentSeekSeconds: null };
  }
  const liveUntilSec = liveUntilSecFromEventLike(eventResults);
  if (liveUntilSec != null && Number.isFinite(liveUntilSec) && serverNowSec >= liveUntilSec) {
    return { elapsed: 0, joiningLate: false, initialSeekTime: null, initialSegmentIndex: null, initialSegmentSeekSeconds: null };
  }
  const elapsed = Math.max(0, Math.min(serverNowSec - eventTs, duration));
  const joiningLate = elapsed >= Math.max(MINI_TV_JOIN_LATE_GRACE_SEC, Math.ceil(MINI_TV_MIN_PRESENTATION_MS / 1000));
  const sid = Number(sportId);

  if (RACING_SPORT_IDS.includes(sid)) {
    return { elapsed, joiningLate, initialSeekTime: joiningLate ? elapsed : null, initialSegmentIndex: null, initialSegmentSeekSeconds: null };
  }
  if (sid === BASEBALL_SPORT_ID || sid === FOOTBALL_SPORT_ID) {
    const startTimes = Array.isArray((eventResults as Record<string, unknown>)?.video_start_time_sequence)
      ? (eventResults as Record<string, unknown[]>).video_start_time_sequence
      : [];
    if (startTimes.length === 0) {
      return { elapsed, joiningLate, initialSeekTime: null, initialSegmentIndex: 0, initialSegmentSeekSeconds: 0 };
    }
    let i = 0;
    for (let k = 0; k < startTimes.length; k++) {
      if (Number(startTimes[k]) <= elapsed) i = k;
    }
    const maxSegmentIndex = Array.isArray((eventResults as Record<string, unknown>)?.video_id_sequence)
      ? Math.max(0, ((eventResults as Record<string, unknown[]>).video_id_sequence).length - 1)
      : startTimes.length - 1;
    if (i > maxSegmentIndex) i = maxSegmentIndex;
    const segmentStart = Number(startTimes[i]) || 0;
    const numSegments = Math.max(1, startTimes.length);
    const maxInSegment = duration != null && Number.isFinite(duration) ? Math.ceil(duration / numSegments) : 3600;
    const inSegment = Math.max(0, Math.min(elapsed - segmentStart, maxInSegment));
    return {
      elapsed,
      joiningLate,
      initialSeekTime: null,
      initialSegmentIndex: i,
      initialSegmentSeekSeconds: joiningLate ? inSegment : 0,
    };
  }
  return { elapsed, joiningLate, initialSeekTime: null, initialSegmentIndex: null, initialSegmentSeekSeconds: null };
}

// ---------------------------------------------------------------------------
// Helper: build run payload
// ---------------------------------------------------------------------------

function buildRunPayload(
  p: UseMiniTvOrchestratorParams,
  joinPosition: JoinPosition,
  showevolution: number | undefined,
): RunPayload {
  // Optional: no results yet (e.g. presentation phase before fetch completes)
  const resultsObj = Object.keys(p.effectiveEventResults ?? {}).length > 0 ? p.effectiveEventResults : {};
  return {
    eventId: p.liveEventId ?? p.nextEventId,
    scheduleId: p.scheduleId,
    sportId: p.sportId,
    event: (p.nextEvent as Record<string, unknown>)?.event ?? p.nextEvent,
    eventResults: {
      ...resultsObj,
      event_id: p.liveEventId ?? p.nextEventId,
      sport_id: p.sportId,
    },
    videoUrl: p.miniTv.videoUrl,
    videoUrls: p.miniTv.videoUrls ?? null,
    avh: p.miniTv.avh,
    showevolution,
    joinElapsedSeconds: joinPosition.joiningLate ? joinPosition.elapsed : 0,
    initialSeekTime: joinPosition.initialSeekTime ?? undefined,
    initialSegmentIndex: joinPosition.initialSegmentIndex ?? undefined,
    initialSegmentSeekSeconds: joinPosition.initialSegmentSeekSeconds ?? undefined,
    ...(Number(p.sportId) === FOOTBALL_SPORT_ID ? { otherWeekEvents: p.footballOtherWeekEvents, weekResults: p.footballWeekResults } : {}),
  };
}

// ---------------------------------------------------------------------------
// useMiniTvOrchestrator
// ---------------------------------------------------------------------------

export function useMiniTvOrchestrator(p: UseMiniTvOrchestratorParams): UseMiniTvOrchestratorResult {
  const presentationStartedAtRef = React.useRef<number | null>(null);
  const miniTvStartIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const startedRunCtxKeyRef = React.useRef<string | null>(null);
  const autoAdvanceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Expose refs for schedule-change cleanup (called externally via resetOrchestrator)
  const resetOrchestrator = React.useCallback(() => {
    if (miniTvStartIntervalRef.current != null) {
      clearInterval(miniTvStartIntervalRef.current);
      miniTvStartIntervalRef.current = null;
    }
    startedRunCtxKeyRef.current = null;
    presentationStartedAtRef.current = null;
    if (autoAdvanceTimerRef.current != null) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, []);

  // ---- Visibility recovery: clean slate on tab return ----

  const prevIsTabVisibleRef = React.useRef(p.isTabVisible);
  React.useEffect(() => {
    const wasHidden = !prevIsTabVisibleRef.current;
    prevIsTabVisibleRef.current = p.isTabVisible;
    if (!p.isTabVisible || !wasHidden) return;

    // Tab just became visible after being hidden. Reset orchestrator state
    // so fresh data (from rejoin queries) drives the next transition.
    if (miniTvStartIntervalRef.current != null) {
      clearInterval(miniTvStartIntervalRef.current);
      miniTvStartIntervalRef.current = null;
    }
    if (autoAdvanceTimerRef.current != null) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    startedRunCtxKeyRef.current = null;
    presentationStartedAtRef.current = Date.now();
  }, [p.isTabVisible]);

  // ---- Derived values ----

  const hasVideoUrls = Boolean(p.miniTv.videoUrl && (!p.useBatchVod || (p.miniTv.videoUrls && p.miniTv.videoUrls.length > 0)));

  const shouldShowCancelledOverlay = Boolean(p.eventStarted && p.tvAccordionExpanded && !p.run && p.isCancelled);

  const presentationElapsedMs =
    p.miniTv.phase === 'presentation' && presentationStartedAtRef.current != null
      ? Date.now() - presentationStartedAtRef.current
      : 0;
  const pastPresentationVideoGrace = presentationElapsedMs >= PRESENTATION_VIDEO_LOAD_GRACE_MS;

  const shouldShowVideoErrorOverlay = Boolean(
    p.eventStarted &&
      p.tvAccordionExpanded &&
      !p.run &&
      !p.isCancelled &&
      // Optional: no results yet
      Object.keys(p.effectiveEventResults ?? {}).length > 0 &&
      !hasVideoUrls &&
      (p.miniTv.phase !== 'presentation' || pastPresentationVideoGrace)
  );

  const shouldShowResultsFetchError = Boolean(
    p.eventStarted && p.tvAccordionExpanded && !p.run && !p.isCancelled && p.pollExhausted
  );

  const joinPositionForPresentation = React.useMemo(() => {
    // Optional: no results yet
    const results = Object.keys(p.effectiveEventResults ?? {}).length > 0 ? p.effectiveEventResults : {};
    return computeJoinPosition(results, p.nowSecRef.current, p.sportId);
  }, [p.effectiveEventResults, p.nowSecRef.current, p.sportId]);

  const vod = (p.effectiveEventResults as Record<string, unknown> | null)?.vod as Record<string, unknown> | undefined;
  const hasValidJoinPositionData =
    p.effectiveEventResults != null &&
    typeof p.effectiveEventResults === 'object' &&
    ((p.effectiveEventResults.event_timestamp != null &&
      (p.effectiveEventResults.duration != null || p.effectiveEventResults.end_timestamp != null)) ||
      (Number(p.sportId) === FOOTBALL_SPORT_ID &&
        Array.isArray(vod?.urls) &&
        (vod!.urls as unknown[]).length > 0));

  // ---- Effect 1: presentation start timestamp + videoReady reset ----

  React.useEffect(() => {
    if (!p.eventStarted) {
      p.miniTv.setVideoReady(false);
      presentationStartedAtRef.current = null;
      return;
    }
    if (presentationStartedAtRef.current == null) {
      presentationStartedAtRef.current = Date.now();
    }
  }, [p.eventStarted, p.miniTv]);

  // ---- Effect 2: auto-expand accordion on LIVE / run in progress ----

  React.useEffect(() => {
    if (p.eventStarted || (p.run && p.run.phase !== 'ended')) p.setTvAccordionExpanded(true);
  }, [p.eventStarted, p.run, p.setTvAccordionExpanded]);

  // ---- Effect 3: collapse accordion on run end ----

  React.useEffect(() => {
    if (p.run?.phase === 'ended') p.setTvAccordionExpanded(false);
  }, [p.run?.phase, p.setTvAccordionExpanded]);

  // ---- Effect 4: auto-advance after 30s for cancelled / video error ----

  React.useEffect(() => {
    if (autoAdvanceTimerRef.current != null) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    if (!p.isTabVisible || (!shouldShowCancelledOverlay && !shouldShowVideoErrorOverlay && !shouldShowResultsFetchError)) return;
    autoAdvanceTimerRef.current = setTimeout(() => {
      autoAdvanceTimerRef.current = null;
      p.advanceCursorAfterExecution({
        eventId: p.liveEventId,
        eventTimestamp: p.nextEvent?.event_timestamp as number | undefined,
        week: p.nextEvent?.week_id as number | undefined,
      });
      p.maybeRefetchUpcoming?.();
    }, 30_000);
    return () => {
      if (autoAdvanceTimerRef.current != null) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    };
  }, [shouldShowCancelledOverlay, shouldShowVideoErrorOverlay, shouldShowResultsFetchError, p.isTabVisible, p.liveEventId, p.nextEvent?.event_timestamp, p.nextEvent?.week_id, p.advanceCursorAfterExecution]);

  // ---- Effect 5: presentation-to-execution transition ----

  React.useEffect(() => {
    const requestKey = p.miniTvCtxKey;
    if (p.run || !p.eventStarted || p.miniTv.phase !== 'presentation') return;
    if (!p.isTabVisible) return;
    if (!hasVideoUrls) return;
    if (p.isSegmentBasedSport && (!p.miniTv.videoUrls || p.miniTv.videoUrls.length === 0)) return;

    // Optional: no results yet
    const resultsForJoin = Object.keys(p.effectiveEventResults ?? {}).length > 0 ? p.effectiveEventResults : {};
    const joinPosition = computeJoinPosition(resultsForJoin, p.nowSecRef.current, p.sportId);

    if (joinPosition.joiningLate) {
      const showevolution =
        (p.effectiveEventResults?.showevolution != null)
          ? Number(p.effectiveEventResults.showevolution)
          : 1;
      const payload = buildRunPayload(p, joinPosition, showevolution);
      if (p.miniTvCtxKeyRef.current !== requestKey) return;
      if (startedRunCtxKeyRef.current === requestKey) return;
      if (payload.scheduleId == null || String(payload.scheduleId).trim() === '') return;
      if (String(payload.scheduleId) !== String(p.scheduleId)) return;
      if (safeEventIdForLabel(payload.eventId) !== safeEventIdForLabel(p.liveEventId ?? p.nextEventId)) return;
      p.startRun(p.scheduleId!, payload);
      startedRunCtxKeyRef.current = requestKey;
      return;
    }

    if (p.isSegmentBasedSport) {
      if (!p.miniTv.videoReady) return;
    } else if (!p.miniTv.videoReady && !p.miniTv.videoUrl) {
      return;
    }
    if (miniTvStartIntervalRef.current != null) {
      clearInterval(miniTvStartIntervalRef.current);
      miniTvStartIntervalRef.current = null;
    }
    const id = setInterval(() => {
      const elapsed = Date.now() - (presentationStartedAtRef.current || 0);
      if (elapsed >= MINI_TV_MIN_PRESENTATION_MS) {
        if (p.miniTvCtxKeyRef.current !== requestKey) return;
        const showevolution =
          (p.effectiveEventResults?.showevolution != null)
            ? Number(p.effectiveEventResults.showevolution)
            : 1;
        const payload = buildRunPayload(p, { elapsed: 0, joiningLate: false, initialSeekTime: null, initialSegmentIndex: null, initialSegmentSeekSeconds: null }, showevolution);
        payload.joinElapsedSeconds = 0;
        if (startedRunCtxKeyRef.current === requestKey) return;
        if (payload.scheduleId == null || String(payload.scheduleId).trim() === '') return;
        if (String(payload.scheduleId) !== String(p.scheduleId)) return;
        if (safeEventIdForLabel(payload.eventId) !== safeEventIdForLabel(p.liveEventId ?? p.nextEventId)) return;
        p.startRun(p.scheduleId!, payload);
        startedRunCtxKeyRef.current = requestKey;
      }
    }, 200);
    miniTvStartIntervalRef.current = id;
    return () => {
      clearInterval(id);
      if (miniTvStartIntervalRef.current === id) miniTvStartIntervalRef.current = null;
    };
  }, [p.miniTvCtxKey, p.run, p.eventStarted, p.miniTv.phase, p.miniTv.videoReady, p.miniTv.videoUrl, hasVideoUrls, p.useBatchVod, p.miniTv.videoUrls, p.sportId, p.liveEventId, p.nextEventId, p.scheduleId, p.nextEvent, p.effectiveEventResults, p.miniTv.avh, p.startRun, p.footballOtherWeekEvents, p.footballWeekResults, p.isSegmentBasedSport, p.isTabVisible]);

  // ---- displayExecutionRuns ----

  const displayExecutionRuns = React.useMemo<DisplayExecutionRun[]>(() => {
    if (p.activeExecutionRuns.length > 0) {
      return p.activeExecutionRuns;
    }
    const runPhase = (p.run as MiniTvRunPhase | null)?.phase;
    if (p.run != null && p.scheduleId != null && runPhase === 'execution' && String((p.run as Record<string, unknown>).scheduleId ?? p.scheduleId) === String(p.scheduleId)) {
      return [{ scheduleId: p.scheduleId, run: p.run }];
    }
    const joinLatePreRun =
      !p.run &&
      p.eventStarted &&
      p.tvAccordionExpanded &&
      p.isActiveLayout &&
      joinPositionForPresentation.joiningLate &&
      hasVideoUrls &&
      (!p.isSegmentBasedSport || (p.miniTv.videoUrls && p.miniTv.videoUrls.length > 0)) &&
      !p.isCancelled &&
      p.scheduleId != null &&
      String(p.scheduleId).trim() !== '';
    if (!joinLatePreRun) {
      return [];
    }
    const showevolution =
      (p.effectiveEventResults?.showevolution != null) ? Number(p.effectiveEventResults.showevolution) : undefined;
    const syntheticRun = buildRunPayload(p, joinPositionForPresentation, showevolution);
    return [{ scheduleId: p.scheduleId!, run: syntheticRun }];
  }, [
    p.activeExecutionRuns,
    p.run,
    p.eventStarted,
    p.tvAccordionExpanded,
    p.isActiveLayout,
    joinPositionForPresentation.joiningLate,
    joinPositionForPresentation.elapsed,
    joinPositionForPresentation.initialSeekTime,
    joinPositionForPresentation.initialSegmentIndex,
    joinPositionForPresentation.initialSegmentSeekSeconds,
    hasVideoUrls,
    p.isSegmentBasedSport,
    p.miniTv.videoUrls,
    p.miniTv.videoUrl,
    p.miniTv.avh,
    p.isCancelled,
    p.scheduleId,
    p.liveEventId,
    p.nextEventId,
    p.nextEvent,
    p.effectiveEventResults,
    p.sportId,
    p.footballOtherWeekEvents,
    p.footballWeekResults,
  ]);

  return {
    displayExecutionRuns,
    shouldShowCancelledOverlay,
    shouldShowVideoErrorOverlay,
    shouldShowResultsFetchError,
    hasVideoUrls,
    joinPositionForPresentation,
    hasValidJoinPositionData,
    resetOrchestrator,
  };
}
